import { cn } from '@/lib/cn';

export type LoadingSpinnerSize = 16 | 20 | 24;

const sizeClasses: Record<LoadingSpinnerSize, string> = {
  16: 'h-4 w-4 border-[1.5px]',
  20: 'h-5 w-5 border-2',
  24: 'h-6 w-6 border-2',
};

export interface LoadingSpinnerProps {
  size?: LoadingSpinnerSize;
  label?: string;
  className?: string;
  /** When true, renders only the spinning indicator without layout wrapper. */
  inline?: boolean;
}

export function LoadingSpinner({
  size = 24,
  label,
  className,
  inline = false,
}: LoadingSpinnerProps) {
  const indicator = (
    <span
      className={cn(
        'inline-block animate-spin rounded-full border-[var(--accent)] border-t-transparent',
        sizeClasses[size],
        className,
      )}
      aria-hidden="true"
    />
  );

  if (inline) {
    return indicator;
  }

  return (
    <div
      className="flex items-center justify-center gap-3 py-12"
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
    >
      {indicator}
      {label ? <span className="text-secondary">{label}</span> : null}
    </div>
  );
}

/** Inline spinner for buttons and compact surfaces. */
export function SpinnerIndicator({
  size = 20,
  className,
}: {
  size?: LoadingSpinnerSize;
  className?: string;
}) {
  return <LoadingSpinner size={size} inline className={className} />;
}
